/* eslint-disable no-debugger */
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { merge, get } from 'lodash';
import { decodeJwt } from 'jose';

import { setTimumApiHost } from '@timum/timum_pdk/src';

import {
  selectIsThemingAllowed,
  selectAreCustomFieldsAllowed,
  selectIsLocalisationAllowed,
  setAppConfig,
  setFcConfig,
  setMuiTheme,
  selectProduct,
} from '../../slices/appState';
import { primary, STANDARD_FIELD_NAMES } from '../../defaults';
import {
  ensureArray,
  transformRefArray,
  normalizeHeight,
  getCookie,
  getVisibleTld,
  isCountryCodeTLD,
} from '../../utils/configUtils';
import { isPresetThemeSync } from '../../themePresetHashes';

const timumDomains =
  'timum\\.de|timum\\.at|timum\\.com|timum\\.fr|timum\\.io|timum\\.es|timum\\.it|timum\\.co.uktimum\\.nl|timum\\.eu|timum\\.info|timum\\.org|timum\\.me';
const allowedOrigin = `(${timumDomains}|localhost(:[0-9]+)?|127\\.0\\.0\\.1(:[0-9]+)?)`;
const allowedOriginRegex = new RegExp(
  `^https?://([a-zA-Z0-9-]+\\.)*${allowedOrigin}$`,
);

function removeUndefinedFields(fields, mergedFields) {
  if (!fields) return;
  const fieldNames = STANDARD_FIELD_NAMES;
  for (const fieldName of fieldNames) {
    if (Object.hasOwn(fields, fieldName) && fields[fieldName] === undefined) {
      delete mergedFields[fieldName];
    }
  }
}

function applyMobileDefaults(tld, mergedAppConfig, givenAppConfig) {
  // omit if field was turned off deliberatly
  const mobileValue = get(givenAppConfig, 'fields.mobile', undefined);
  if (mobileValue === undefined) {
    return;
  }
  if (typeof tld !== 'string') return;
  if (tld === 'localhost') return;

  // enhance preferredCountries if it is unchanged from the default...
  if (!get(givenAppConfig, 'fields.mobile.preferredCountries', undefined)) {
    // ... and the tld is parseable
    if (isCountryCodeTLD(tld.toUpperCase())) {
      mergedAppConfig.fields.mobile.preferredCountries = [
        tld.toUpperCase(),
        ...primary.appConfig.fields.mobile.preferredCountries,
      ];
    }
  }
  // Set defaultCountry if it doesn't exist (is null/undefined/falsy)...
  if (!get(givenAppConfig, 'fields.mobile.defaultCountry', undefined)) {
    // ... and the tld is parseable
    if (isCountryCodeTLD(tld.toUpperCase())) {
      mergedAppConfig.fields.mobile.defaultCountry = tld.toUpperCase();
    }
  }
}

function sortFields(fields, index, result) {
  if (!result) {
    result = {};
  }

  if (typeof index === 'undefined') {
    index = 0;
  }

  if (index >= Object.entries(fields).length) {
    return result;
  }

  for (const [key, value] of Object.entries(fields)) {
    if (value && value.index === index) {
      result[key] = value;
    }
  }

  index++;
  return sortFields(fields, index, result);
}

function transformRef(ref, platform, prvUuid) {
  if (
    !ref.includes('@') &&
    !ref.match(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    )
  ) {
    if (!platform) {
      console.warn(
        "One or more ref parameters aren't fully qualified AND you have not provided a `platform` prop which allows timum to do it for you. " +
          "Most likely timum won't find anything. " +
          'Please either add a platform prop to your configuration or use a full reference in either the ref prop or a ref url parameter.',
      );
    } else {
      return `${ref}${prvUuid ? '@' + prvUuid : ''}@${platform}`;
    }
  }

  return ref;
}

function addPostMessageCallbacks(appConfig) {
  const postMessage = (type, payload = {}) => {
    window?.parent?.postMessage(
      { origin: 'bookingjs', type, ...payload },
      appConfig.postMessageTarget,
    );
  };

  // Factory for callbacks that spread their payload directly
  const createSpreadCallback = (type) => (payload) =>
    postMessage(type, payload);

  // Factory for callbacks that wrap arg as 'data'
  const createDataCallback = (type) => (data) => postMessage(type, { data });

  // Factory for callbacks that wrap arg as 'error'
  const createErrorCallback = (type) => (error) => postMessage(type, { error });

  // Factory for callbacks that wrap arg as 'timeslot'
  const createTimeslotCallback = (type) => (tsl) =>
    postMessage(type, { timeslot: tsl });

  // Factory for callbacks with no args
  const createSimpleCallback = (type) => () => postMessage(type);

  appConfig.callbacks = {
    // Booking lifecycle callbacks (spread payload)
    createBookingStarted: createSpreadCallback('createBookingStarted'),
    createBookingSuccessful: createSpreadCallback('createBookingSuccessful'),
    createBookingFailed: createSpreadCallback('createBookingFailed'),

    // Cancelation lifecycle callbacks (spread payload)
    cancelationStarted: createSpreadCallback('cancelationStarted'),
    cancelationSuccessful: createSpreadCallback('cancelationSuccessful'),
    cancelationFailed: createSpreadCallback('cancelationFailed'),

    // Fetch success callbacks (wrap as data)
    fetchingPublicDataSucceeded: createDataCallback(
      'fetchingPublicDataSucceeded',
    ),
    fetchingProductsSucceeded: createDataCallback('fetchingProductsSucceeded'),
    fetchingBookablesSucceeded: createDataCallback(
      'fetchingBookablesSucceeded',
    ),

    // Fetch failure callbacks (wrap as error)
    fetchingPublicDataFailed: createErrorCallback('fetchingPublicDataFailed'),
    fetchingProductsFailed: createErrorCallback('fetchingProductsFailed'),
    fetchingBookablesFailed: createErrorCallback('fetchingBookablesFailed'),

    // Dialog open/close with timeslot
    openedBookingPage: createTimeslotCallback('openedBookingPage'),
    openedCancelPage: createTimeslotCallback('openedCancelPage'),
    closedBookingPage: createTimeslotCallback('closedBookingPage'),
    closedCancelPage: createTimeslotCallback('closedCancelPage'),

    // Simple dialog open/close (no args)
    openedProductSelection: createSimpleCallback('openedProductSelection'),
    openedResourceSelection: createSimpleCallback('openedResourceSelection'),
    openedConfirmationPage: createSimpleCallback('openedConfirmationPage'),
    closedProductSelection: createSimpleCallback('closedProductSelection'),
    closedResourceSelection: createSimpleCallback('closedResourceSelection'),
    closedConfirmationPage: createSimpleCallback('closedConfirmationPage'),
  };
}

function getJwtConfig(jwt) {
  try {
    return decodeJwt(jwt);
  } catch (error) {
    console.error('Error decrypting JWT:', error);
  }
}

const RESERVED_GLOBAL_KEYS = ['appConfig', 'muiTheme', 'fcConfig'];

/**
 * Reads window.timumBookingConfig. Checks the current window first so
 * consumers that set the global on the frame where ConfigHandler actually
 * runs (e.g. direct init(), unit tests) are covered. Falls back to
 * window.parent so the iframe-wrapped paths (initialise() and <TimumBooking>,
 * which both render inside a same-origin srcdoc iframe) still pick up a
 * global set on the host page. Wrapped in try/catch for defensive handling
 * of cross-origin parents.
 */
function readGlobalBookingConfig() {
  try {
    if (typeof window === 'undefined') return undefined;
    if (window.timumBookingConfig) return window.timumBookingConfig;
    if (window.parent && window.parent !== window) {
      return window.parent.timumBookingConfig;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Layers window.timumBookingConfig slices on top of the given configs.
 * Returns new objects — inputs are not mutated.
 *
 * Precedence (lowest to highest) as produced by this helper:
 *   given props  <  global top-level  <  global per-instance (keyed by rootElId)
 *
 * The returned configs are subsequently fed to validateAndPreProcessConfig,
 * which layers URL parameters on top, yielding the full cascade:
 *   defaults < given props < global top-level < global per-instance < URL.
 *
 * The global layer is bypassed entirely when the widget is rendered via the
 * <TimumBooking> React component (isComponent === true) — React users pass
 * props per call and have no use case for a window-level override.
 *
 * Reserved rootElId values ('appConfig', 'muiTheme', 'fcConfig') would collide
 * with the top-level slots and are skipped with a console.warn.
 */
function applyGlobalOverrides(givenAppConfig, givenMuiTheme, givenFcConfig) {
  if (givenAppConfig?.isComponent) {
    return {
      appConfig: givenAppConfig,
      muiTheme: givenMuiTheme,
      fcConfig: givenFcConfig,
    };
  }

  const globalConfig = readGlobalBookingConfig();
  if (!globalConfig) {
    return {
      appConfig: givenAppConfig,
      muiTheme: givenMuiTheme,
      fcConfig: givenFcConfig,
    };
  }

  const rootElId = givenAppConfig?.rootElId;
  let perInstance;
  if (rootElId) {
    if (RESERVED_GLOBAL_KEYS.includes(rootElId)) {
      console.warn(
        `[bookingjs] rootElId "${rootElId}" collides with a reserved top-level ` +
          `slot in window.timumBookingConfig and cannot be used as a ` +
          `per-instance override key. Skipping per-instance lookup.`,
      );
    } else {
      perInstance = globalConfig[rootElId];
    }
  }

  return {
    appConfig: merge(
      {},
      givenAppConfig,
      globalConfig.appConfig,
      perInstance?.appConfig,
    ),
    muiTheme: merge(
      {},
      givenMuiTheme,
      globalConfig.muiTheme,
      perInstance?.muiTheme,
    ),
    fcConfig: merge(
      {},
      givenFcConfig,
      globalConfig.fcConfig,
      perInstance?.fcConfig,
    ),
  };
}

function validateAndPreProcessConfig(givenAppConfig) {
  const result = { ...givenAppConfig };
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(result.queryString || queryString);

  let urlPData = urlParams.get('pData') ?? urlParams.get('pdata');
  let urlPData2 = urlParams.get('pDataPlatform')
    ? {
        platform: urlParams.get('pDataPlatform'),
        id: urlParams.get('pDataId'),
        idPropName: urlParams.get('pDataIdPropName'),
      }
    : undefined;
  const timumCookie = getCookie('PLAY_SESSION');
  let urlChannelKey =
    urlParams.get('channelKey') ?? urlParams.get('channelkey');
  const urlAuthToken = urlParams.get('X-SIGNED-APP-CUSTOMER');
  let urlResId = urlParams.get('resId'); // part of cancel link
  let urlRef = urlParams.getAll('ref');
  let urlTslRefs = urlParams.getAll('tslRefs');
  let urlVirtualTslRefs = urlParams.getAll('virtualTslRefs');
  let urlPrdRefs = urlParams.getAll('prdRefs');
  let urlCulture = urlParams.get('culture');
  let urlPostMessageTarget = urlParams.get('postMessageTarget');
  let urlAllResourcesOption = urlParams.get('allResourcesOption');
  let urlCalendarFrontend = urlParams.get('calendarFrontend');

  // if there is a compressed config, it has priority over manually entered data
  // prevents people fiddling around with their links, as then fiddling causes no changes
  let urlConfig = urlParams.get('conf');
  if (urlConfig) {
    urlConfig = getJwtConfig(urlConfig);
    urlRef = urlConfig.ref || [];
    urlChannelKey = urlConfig.channelKey;
    urlPData = urlConfig.pData;
    urlTslRefs = urlConfig.tslRefs || [];
    urlPrdRefs = urlConfig.prdRefs || [];
    urlCulture = urlConfig.urlCulture;
    urlVirtualTslRefs = urlConfig.virtualTslRefs || [];
    urlPostMessageTarget = urlConfig.postMessageTarget;
    urlAllResourcesOption = urlConfig.allResourcesOption;
    urlCalendarFrontend = urlConfig.calendarFrontend;
  }

  const hash = result.queryHash || window.location.hash;
  const [, , appointmentUuid] = hash?.split('/') ?? [];

  if (urlPData || urlPData2) {
    result.pData = urlPData ?? urlPData2;
  }

  if (urlChannelKey) {
    result.channelKey = urlChannelKey;
  }

  if (urlRef && urlRef.length > 0) {
    result.ref = urlRef;
  }

  if (urlTslRefs && urlTslRefs.length > 0) {
    result.tslRefs = urlTslRefs;
  }

  if (urlVirtualTslRefs && urlVirtualTslRefs.length > 0) {
    result.virtualTslRefs = urlVirtualTslRefs;
  }

  if (urlPrdRefs && urlPrdRefs.length > 0) {
    result.prdRefs = urlPrdRefs;
  }

  if (urlCulture) {
    result.culture = urlCulture;
  }

  if (urlPostMessageTarget) {
    result.postMessageTarget = urlPostMessageTarget;
  }

  if (urlAllResourcesOption) {
    result.allResourcesOption = urlAllResourcesOption;
  }

  if (urlCalendarFrontend) {
    result.calendarFrontend = urlCalendarFrontend;
  }

  // Ensure ref arrays are proper arrays (handles frozen arrays and single values)
  result.ref = ensureArray(result.ref);
  result.tslRefs = ensureArray(result.tslRefs);
  result.prdRefs = ensureArray(result.prdRefs);

  // don't render anything if...
  if (
    !result.alwaysRender &&
    result.isEmbed &&
    result.hiddenForAnonymous &&
    (!result.pData || !result.pData.id)
  ) {
    console.log(
      'timum hides itself. We are in an embed, are set to hide wihtout pData and cannot find none neither in url nor in config',
    );
    return;
  }

  if (
    !result.alwaysRender &&
    (!result.ref || result.ref.length === 0) &&
    (!result.tslRefs || result.tslRefs.length === 0) &&
    (!result.virtualTslRefs || result.virtualTslRefs.length === 0)
  ) {
    // don't render anything if...
    console.log(
      'timum hides itself. No reference in either url or options detected.',
    );
    return;
  }

  // Transform refs to fully qualified format
  result.ref = transformRefArray(
    result.ref,
    result.platform,
    result.prvUuid,
    transformRef,
  );
  result.tslRefs = transformRefArray(
    result.tslRefs,
    result.platform,
    result.prvUuid,
    transformRef,
  );
  result.prdRefs = transformRefArray(
    result.prdRefs,
    result.platform,
    result.prvUuid,
    transformRef,
  );

  if (timumCookie) {
    result.cookie = timumCookie;
  }

  if (urlAuthToken) {
    result.cancelableAppointment = {
      authToken: urlAuthToken,
      appointmentUuid: appointmentUuid,
      customerUuid: urlPData2.id,
      resourceUuid: urlResId,
    };
  }

  if (result.host) {
    setTimumApiHost(result.host);
  }

  if (result.height) {
    result.height = normalizeHeight(result.height);
  }

  return result;
}

/**
 * fills passed in configs with missing props without overriding the ones set externally
 */
const mergeConfigs = (
  givenAppConfig,
  givenMuiTheme,
  givenFcConfig,
  isThemingAllowed,
  areCustomFieldsAllowed,
  isLocalisationAllowed,
) => {
  const hasCustomMuiTheme = Object.keys(givenMuiTheme).length > 0;
  const hasCustomAppConfig = Object.keys(givenAppConfig).length > 0;
  let mergedAppConfig = merge({}, primary.appConfig, givenAppConfig);
  let mergedMuiTheme = merge({}, primary.muiTheme, givenMuiTheme);
  let mergedFcConfig = merge({}, primary.fcConfig, givenFcConfig);
  let tld = getVisibleTld();

  // orders input fields according to their index
  mergedAppConfig.fields = sortFields(mergedAppConfig.fields);

  // remove fields if they have been set to undefined
  removeUndefinedFields(givenAppConfig.fields, mergedAppConfig.fields);

  // if customer defined fontSource, remove our default font faces.
  if (mergedMuiTheme?.typography?.fontSource) {
    delete mergedMuiTheme.typography.fontFaces;
  }

  // and now reset everyhing you just did, if they haven't paid for it
  if (hasCustomAppConfig && !isLocalisationAllowed) {
    mergedAppConfig.localization = primary.appConfig.localization;
  }
  if (hasCustomAppConfig && !areCustomFieldsAllowed) {
    mergedAppConfig.fields = primary.appConfig.fields;
  }

  if (hasCustomAppConfig && !isThemingAllowed) {
    mergedAppConfig.hideTimumFooter = primary.appConfig.hideTimumFooter;
  }

  if (hasCustomMuiTheme && !isThemingAllowed && !isPresetThemeSync(givenMuiTheme)) {
    mergedMuiTheme = primary.muiTheme;
  }

  if (mergedAppConfig.postMessageTarget) {
    addPostMessageCallbacks(mergedAppConfig);
  }

  // adapt default mobile country code and preferred country based on tld
  applyMobileDefaults(tld, mergedAppConfig, givenAppConfig);

  return {
    mergedAppConfig,
    mergedMuiTheme,
    mergedFcConfig,
  };
};

function dispatchConfigs({ appConfig, muiTheme, fcConfig, dispatch }) {
  dispatch(setFcConfig(fcConfig));
  dispatch(setAppConfig(appConfig));
  dispatch(setMuiTheme(muiTheme));
}

const ConfigHandler = ({
  muiTheme: muiThemeControlled = {},
  fcConfig: fcConfigControlled = {},
  appConfig: appConfigControlled = {},
  children,
}) => {
  const dispatch = useDispatch();
  const [appConfig, setAppConfig] = React.useState(appConfigControlled);
  const [fcConfig, setFcConfig] = React.useState(fcConfigControlled);
  const [muiTheme, setMuiTheme] = React.useState(muiThemeControlled);
  const isThemingAllowed = useSelector(selectIsThemingAllowed);
  const areCustomFieldsAllowed = useSelector(selectAreCustomFieldsAllowed);
  const isLocalisationAllowed = useSelector(selectIsLocalisationAllowed);

  const doRerender = useCallback(
    (
      newAppConfig = appConfig,
      newMuiTheme = muiTheme,
      newFcConfig = fcConfig,
    ) => {
      // Note: window.timumBookingConfig is read fresh on every call — it is
      // not a React dependency. The global is expected to be set before init
      // and not hot-swapped; postMessage-triggered rerenders still pick up
      // changes because this callback re-runs applyGlobalOverrides each time.
      const {
        appConfig: globalizedAppConfig,
        muiTheme: globalizedMuiTheme,
        fcConfig: globalizedFcConfig,
      } = applyGlobalOverrides(newAppConfig, newMuiTheme, newFcConfig);
      const processedConfig = validateAndPreProcessConfig(globalizedAppConfig);

      if (!processedConfig) return;

      dispatch(selectProduct(undefined));

      const { mergedAppConfig, mergedMuiTheme, mergedFcConfig } = mergeConfigs(
        processedConfig,
        globalizedMuiTheme,
        globalizedFcConfig,
        isThemingAllowed,
        areCustomFieldsAllowed,
        isLocalisationAllowed,
      );

      // Note: fetchReferencedData is NOT called here anymore.
      // Data fetching is now handled by a dedicated useEffect that only triggers
      // when data-dependent props change (ref, prdRefs, tslRefs, etc.),
      // not when styling props change (muiTheme, height, etc.)

      dispatchConfigs({
        appConfig: mergedAppConfig,
        muiTheme: mergedMuiTheme,
        fcConfig: mergedFcConfig,
        dispatch,
      });
    },
    [
      appConfig,
      muiTheme,
      fcConfig,
      isThemingAllowed,
      areCustomFieldsAllowed,
      isLocalisationAllowed,
      dispatch,
    ],
  );

  useEffect(() => {
    const eventHandler = (event) => {
      if (
        allowedOriginRegex.test(event.origin) &&
        event.data?.type === 'rerender'
      ) {
        const currentTheme = event.data?.muiTheme || muiTheme;
        const currentAppConfig = event.data?.appConfig || appConfig;
        const currentFcConfig = event.data?.fcConfig || fcConfig;

        setAppConfig(currentAppConfig);
        setMuiTheme(currentTheme);
        setFcConfig(currentFcConfig);

        doRerender(
          { ...currentAppConfig },
          { ...currentTheme },
          { ...currentFcConfig },
        );
      } else if (event.data?.type === 'rerender') {
        console.error(
          'ConfigHandler received a message from an unexpected origin:',
          event.origin,
        );
      }
    };

    window.addEventListener('message', eventHandler);

    window?.parent?.postMessage(
      {
        origin: 'bookingjs',
        type: 'ready',
      },
      appConfig.postMessageTarget || '*',
    );

    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, [doRerender, appConfig, muiTheme, fcConfig]);

  useEffect(() => {
    const {
      appConfig: globalizedAppConfig,
      muiTheme: globalizedMuiTheme,
      fcConfig: globalizedFcConfig,
    } = applyGlobalOverrides(appConfig, muiTheme, fcConfig);
    const processedConfig = validateAndPreProcessConfig(globalizedAppConfig);

    if (!processedConfig) return;

    const { mergedAppConfig, mergedMuiTheme, mergedFcConfig } = mergeConfigs(
      processedConfig,
      globalizedMuiTheme,
      globalizedFcConfig,
      isThemingAllowed,
      areCustomFieldsAllowed,
      isLocalisationAllowed,
    );

    dispatchConfigs({
      appConfig: mergedAppConfig,
      muiTheme: mergedMuiTheme,
      fcConfig: mergedFcConfig,
      dispatch,
    });
  }, [
    appConfig,
    muiTheme,
    fcConfig,
    dispatch,
    isThemingAllowed,
    areCustomFieldsAllowed,
    isLocalisationAllowed,
  ]);

  return children;
};

ConfigHandler.displayName = 'ConfigHandler';
export default ConfigHandler;
